import { FieldViewModel, DrawingLayer, fields, IDataLayer, IDatalayerContext, IDataLayerZone, IGeometry, models, utils } from "../../omnia"
import { EditFeature } from "./edit-feature"
import { getDefaultLabelLine, UndockedLabel } from "./label"
import { INewZoneOptions } from "./types"

/// <reference path="edit-feature.ts" />
/// <reference path="label.ts" />

import LayerZoneTypeId = models.LayerZoneTypeId

import FieldsLayer = fields.map.layers.FieldsLayer
import { ol_Coordinate, ol_Feature, ol_geom } from "@omnia/exports/openlayers"


export class EditLayer extends DrawingLayer<EditFeature> {

    constructor(datalayer: IDataLayer, _context: IDatalayerContext, private fields: FieldsLayer) {
        super(datalayer, _context)
        this.init()
    }

    private init() {
        this.setupFieldFeatures(this.fields)
        this.zones.forEach(z => z.setupUndockedLabel())
    }

    copyZones(zones: IDataLayerZone[], includeValue: boolean, clipping?: (geom: IGeometry) => IGeometry) {
        super.copyZones(zones, includeValue, clipping)
        this.setupFieldZones(this._zones as EditFeature[])
        this.init()
    }

    protected createZoneInstance(zone: IDataLayerZone) {
        return new EditFeature(this, zone)
    }

    createZone(feature: ol_Feature, options?: INewZoneOptions, factory?: (layer: EditLayer, model: IDataLayerZone) => EditFeature) {

        options = options || {}
        factory = factory || ((layer, model) => new EditFeature(layer, model))

        const model = <IDataLayerZone>{
            DataLayerZoneId: 0,
            Geometry: null,
            Buffer: options.buffer,
            TypeId: options.type
        }

        const zone = factory(this, model)

        this.setZoneNumber(zone)

        this._zones.push(zone)

        if (options.label) {
            zone.label = utils.naming.getUniqueName(this.zones, "label", options.label);
        }

        if (options.defaultColor) {
            zone.fillColor = options.defaultColor
        }

        zone.setFeature(feature)

        zone.commitState()

        return zone

    }

    get zones(): EditFeature[] {
        return <EditFeature[]>(this._zones);
    }

    getUpdateModels() {
        const zones = this.zones.concat(this._fieldZones).filter(f => f.shouldSave())
        return this.getUpdateModelsSafe(zones)
    }

    getFieldFeature(field: FieldViewModel, sourceFeature: ol_Feature, add: boolean) {

        let zone = this._fieldZones.find(f => f.isFieldFeature(field.fieldId))
        if (zone) return zone

        if (add) {
            zone = this.addFieldZone(field, sourceFeature)
        }

        return zone

    }

    protected setupFieldFeatures(fields: FieldsLayer) {

        fields.getFeatures().forEach(f => {

            const field = <FieldViewModel>f.get("data")

            const zone = this._fieldZones.find(f => f.fieldId == field.fieldId)

            if (zone) {
                zone.zone.Label = field.fieldName
                this.setupFieldFeature(zone, f)
            }
            else {
                this.addFieldZone(field, f)
            }

        })

    }

    private addFieldZone(field: FieldViewModel, sourceFeature: ol_Feature) {

        const zone = EditFeature.createFieldFeature(this, field.fieldId, field.fieldName)
        this._fieldZones.push(zone)

        this.setupFieldFeature(zone, sourceFeature)

        return zone

    }

    private setupFieldFeature(zone: EditFeature, sourceFeature: ol_Feature) {
        const mapFeature = new ol_Feature((<any>sourceFeature.getGeometry()).clone())
        zone.setFeature(mapFeature)
        this._source.addFeature(mapFeature)
    }

    hasVisibleFieldFeatures() {
        return this._fieldZones && this._fieldZones.some(f => f.shouldSave())
    }

    createNewText(position: ol_Coordinate)
    createNewText(parent: EditFeature, geometry?: ol_geom.Geometry)
    createNewText(target: ol_Coordinate | EditFeature, geometry?: ol_geom.Geometry) {

        const parent = target instanceof EditFeature ? target : null

        let geom = parent && geometry
            ? geometry
            : getDefaultLabelLine(target instanceof EditFeature
                ? omnia.map.getGeometryCenter(parent.geometry)
                : target
            )

        const feature = this.createNewFeature(geom, null)

        const options = {
            label: parent ? null : "New text",
            type: LayerZoneTypeId.Text
        }

        const factory = parent
            ? (l, m) => new UndockedLabel(parent, m, feature)
            : null

        const text = this.createZone(feature, options, factory)

        return text

    }

}
